import { z } from 'zod';
/**
 * Predefined relationship questions
 */
export const RELATIONSHIP_QUESTIONS = [
    { id: 'good_working_relationship', text: 'Do you have a good working relationship with the client?' },
    { id: 'expecting_major_changes', text: 'Are you expecting major changes in the coming period?' },
    { id: 'adequate_creative_assets', text: 'Do you have adequate creative assets?' },
    { id: 'reporting_confidence', text: 'Do you have confidence in your reporting?' },
    { id: 'abnormal_workload', text: 'Are you planning abnormal workload?' },
    { id: 'can_hit_targets', text: 'Do you believe you can hit your targets?' },
    { id: 'strategy_confidence', text: 'Do you have confidence in your strategy?' }
];
// Zod schemas for validation
const TimestampSchema = z.object({
    seconds: z.number(),
    nanoseconds: z.number(),
    toDate: z.function(),
    toMillis: z.function(),
});
export const FTEInputSchema = z.object({
    user_id: z.string().min(1),
    fte_amount: z.number().min(0).max(100),
    last_updated: TimestampSchema.optional(),
    updated_by: z.string().optional(),
});
export const RelationshipInputSchema = z.object({
    question_id: z.string().min(1),
    response: z.enum(['yes', 'no']),
    last_updated: TimestampSchema.optional(),
    updated_by: z.string().optional(),
});
export const ObjectiveInputSchema = z.object({
    objective: z.enum(['roas', 'cpa', 'revenue', 'conversions']),
    objective_type: z.enum(['efficiency', 'volume']),
    objective_goal: z.number(),
    objective_result: z.number().optional(),
    last_updated: TimestampSchema.optional(),
    updated_by: z.string().optional(),
});
export const SpendInputEntrySchema = z.object({
    deal_id: z.string().min(1),
    deal_name: z.string().min(1),
    description: z.string(),
    entries: z.string(),
    channel: z.string().min(1),
    tactic: z.string().min(1),
    currency: z.string().min(1),
    amount: z.number().min(0),
    status: z.enum(['pending', 'complete', 'approved', 'needs-review', 'rejected']).optional(),
    last_updated: TimestampSchema.optional(),
    updated_by: z.string().optional(),
    approval_id: z.string().optional(),
    approved_at: TimestampSchema.optional(),
});
export const SpendInputSchema = z.object({
    entries: z.array(SpendInputEntrySchema),
    total_spend: z.number().optional(),
    primary_currency: z.string().optional(),
});
export const SpendFluctuationsSchema = z.object({
    context: z.string().min(1),
    last_updated: TimestampSchema.optional(),
    updated_by: z.string().optional(),
});
export const WorkflowDataSubmissionSchema = z.object({
    id: z.string().min(1),
    workflow_name: z.string().min(1),
    workflow_id: z.string().min(1),
    cycle_id: z.string().min(1),
    fte_input: z.array(FTEInputSchema),
    relationship_input: z.array(RelationshipInputSchema),
    objective_input: z.array(ObjectiveInputSchema),
    spend_input: SpendInputSchema,
    spend_fluctuations: SpendFluctuationsSchema.optional(),
    status: z.enum(['draft', 'in_progress', 'completed', 'submitted']),
    created_at: TimestampSchema,
    updated_at: TimestampSchema,
    version: z.number().min(1),
    team_lead: z.string().optional(),
    manager_override: z.array(z.string()).optional(),
    completion_stats: z.object({
        fte_complete: z.boolean(),
        relationship_complete: z.boolean(),
        objective_complete: z.boolean(),
        spend_complete: z.boolean(),
        spend_review: z.enum(['pending', 'complete', 'needs-review', 'approved']),
        overall_complete: z.boolean(),
    }),
});
// Type guards
export const isValidWorkflowDataSubmission = (data) => {
    return WorkflowDataSubmissionSchema.safeParse(data).success;
};
export const isValidFTEInput = (data) => {
    return FTEInputSchema.safeParse(data).success;
};
export const isValidRelationshipInput = (data) => {
    return RelationshipInputSchema.safeParse(data).success;
};
export const isValidObjectiveInput = (data) => {
    return ObjectiveInputSchema.safeParse(data).success;
};
